import { Popover, Icon, ButtonDropdownProps } from "@amzn/awsui-components-react";
import React, { useEffect, useRef, useState } from "react";
import FormulaExpressionToolbar from "src/components/shared/FormulaExpressionToolbar";
import { TPEReactTable } from "src/components/shared/TPEReactTable";
import CONSTANTS from "src/utils/constants";
import WorksheetTotal from "src/models/tp-allocation/WorksheetTotal";
import { renderExpressionParts } from "src/components/shared/FormulaExpressionFunctionComponents";
import { ACTIONS } from "src/services/tp-allocation/totals/WorksheetTotalsReducer";
import DomUtils from "src/utils/DomUtils";
import { ACTION_TYPE, TPEBasicModal } from "src/components/shared/TPEBasicModal";
import ErrorUtils from "src/utils/ErrorUtils";
import { DasTotalsContext } from "src/services/derived-allocation-sheet/DasTotalsContext";
import { DAS_TOTAL_MENU_ACTIONS, DasTotalRow, dasTotalsCellEditor } from "./DasTotalsCells";
import { getDasTotalsColumns } from "./DasTotalsColumns";
import { totalsReferencing } from "./dasTotalChecks";

function ValidationErrors({ total }: { total: WorksheetTotal }) {
    const errors = (total.validationErrors || []).map((e, i) => <li key={i}>{e.message}</li>);
    if (errors.length === 0) {
        return null;
    }
    return (
        <Popover className="errorMessagePopover" dismissButton={false} position="top" size="small" triggerType="custom"
                 content={<React.Fragment><b>Validation Errors</b><ul>{errors}</ul></React.Fragment>}>
            <Icon name="status-warning" size="normal" variant="warning" />
        </Popover>
    );
}

/**
 * Step 2 totals grid of the derived allocation sheet builder, adapted from the TPA totals grid. Totals are
 * deleted through the derived allocation sheet service; the section adds duplicates. A total that another
 * total refers to cannot be removed. It can be renamed; the backend rewrites the references.
 */
export default function DasTotalsGrid(props: {
    onTotalsLevelError: (x?: string) => void,
    onTotalRemoved: () => void,
    onDuplicate: (total: WorksheetTotal) => void,
    saving?: boolean,
}) {
    const { onTotalsLevelError, onTotalRemoved, onDuplicate, saving = false } = props;
    const { services, totalsState, totalsDispatch, dataSources, viewMode, sheetId } = React.useContext(DasTotalsContext);
    const { totals, showValues } = totalsState;
    const editable = CONSTANTS.VIEW_MODE.EDITABLE === viewMode;
    const [formattedRecords, setFormattedRecords] = useState<DasTotalRow[]>([]);
    const [pendingRemove, setPendingRemove] = useState(undefined as WorksheetTotal | undefined);
    const [isDeleting, setIsDeleting] = useState(false);
    const [skipPageReset, setSkipPageReset] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const dataSourceNames = dataSources.filter((x) => !!x.description).map((x) => ({ name: x.description }));

    const expressionParts = (expression: string) => services.readOnlyCalculationService.breakExpressionIntoParts(
        expression, totals.map((x) => ({ name: x.description, visibleSequence: x.visibleSequence })), dataSourceNames, new Set());

    useEffect(() => {
        if (totals == null) {
            return;
        }
        totals.forEach((t, index) => {
            t.visibleSequence = `${index + 1}`;
            t.sequence = index + 1;
        });
        setFormattedRecords(totals.map((x) => {
            const parts = expressionParts(x.expression);
            x.expressionTokens = parts;
            return {
                ...x,
                isEditable: editable,
                expressionFormatted: renderExpressionParts(parts),
                formulaOutputFormatted: x.value == null || x.value === '' ? '-' : services.formattingService.formatString(String(x.value), true),
                errorIndicator: (x.validationErrors || []).length > 0 ? <ValidationErrors key={`popFormulaError_${x.visibleSequence}`} total={x} /> : null,
            };
        }));
    }, [totals, dataSources, viewMode]);

    useEffect(() => {
        if (!editable) {
            totalsDispatch(ACTIONS.SET_TOTAL_BEING_EDITED.withPayload(null));
        }
    }, [viewMode]);

    const confirmRemove = async () => {
        const total = pendingRemove;
        if (total == null) return;
        onTotalsLevelError(undefined);
        setIsDeleting(true);
        try {
            await services.derivedAllocationSheetService.deleteDASTotal({ sheetId, totalId: total.totalId });
            if (!mountedRef.current) return;
            totalsDispatch(ACTIONS.SET_REMOVE_TOTAL.withPayload(total));
            totalsDispatch(ACTIONS.SET_TOTAL_BEING_EDITED.withPayload(null));
            onTotalRemoved();
        } catch (ex) {
            if (mountedRef.current) onTotalsLevelError(ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) {
                setIsDeleting(false);
                setPendingRemove(undefined);
            }
        }
    };

    /** Refuses early when another total uses this one; the backend refuses such a delete too. */
    const usedByMessage = (name: string, self: WorksheetTotal): string | undefined => {
        const users = totalsReferencing(name, totals, self);
        return users.length > 0 ? `"${name}" is used by total(s): ${users.map((t) => t.description).join(', ')}.` : undefined;
    };

    const onMenuClick = (detail: ButtonDropdownProps.ItemClickDetails, total: WorksheetTotal) => {
        switch (detail.id) {
            case DAS_TOTAL_MENU_ACTIONS.EDIT.id:
                totalsDispatch(ACTIONS.SET_TOTAL_BEING_EDITED.withPayload(total));
                break;
            case DAS_TOTAL_MENU_ACTIONS.DUPLICATE.id:
                onDuplicate(total);
                break;
            case DAS_TOTAL_MENU_ACTIONS.REMOVE.id: {
                const usedBy = usedByMessage((total.description || '').trim(), total);
                if (usedBy) {
                    onTotalsLevelError(usedBy);
                } else if (total.isNew) {
                    // Never saved, so there is nothing to delete on the backend.
                    totalsDispatch(ACTIONS.SET_WORKSHEET_TOTALS.withPayload(totals.filter((t) => t.totalId !== total.totalId)));
                    totalsDispatch(ACTIONS.SET_TOTAL_BEING_EDITED.withPayload(null));
                } else {
                    setPendingRemove(total);
                }
                break;
            }
        }
    };

    const columnDefinitions = React.useMemo(() => getDasTotalsColumns({ viewMode, saving, totals, onMenuClick }),
        [totals, viewMode, saving, onDuplicate, onTotalsLevelError]);

    const onRecordUpdated = (rowIndex: number, columnId: string, value?: string) => {
        setSkipPageReset(true);
        // The name is trimmed when the cell is left, so the row keeps the name the backend stores.
        let payload: { field: string, value?: string, extraValue?: unknown };
        if (columnId === CONSTANTS.WORKSHEET_TOTAL_FIELDS.OVERVIEW) {
            payload = { field: columnId, value, extraValue: expressionParts(value ?? '') };
        } else if (columnId === CONSTANTS.WORKSHEET_TOTAL_FIELDS.DESCRIPTION) {
            payload = { field: columnId, value: value?.trim() };
        } else {
            payload = { field: columnId, value };
        }
        totalsDispatch(ACTIONS.UPDATE_CURRENT_TOTAL.withPayload(payload));
    };

    const onRowClicked = (e: React.MouseEvent<HTMLElement>, total: WorksheetTotal) => {
        // Clicks on the output, sequence or error cells do not start editing.
        const target = e.target as HTMLElement;
        if (!editable || !total.isEditable
            || DomUtils.isDescendantOfClassName(target, 'formulaOutputCell')
            || DomUtils.isDescendantOfClassName(target, 'totalSequenceCell')
            || DomUtils.isDescendantOfClassName(target, 'inlineErrorContainer')) {
            return;
        }
        totalsDispatch(ACTIONS.SET_TOTAL_BEING_EDITED.withPayload(total));
    };

    return <div className="dataSourceTableContainer">
        {editable && <FormulaExpressionToolbar showBuiltInFunctions={false} dispatch={totalsDispatch} />}
        <TPEReactTable {...{
            data: formattedRecords,
            columnDefinitions,
            className: "worksheetTotalsGrid",
            sortable: false,
            key: "worksheetTotalsGrid1",
            onRowClicked,
            emptyMessage: editable ? "There are no totals yet. Click Add total to start." : "No totals",
            editableOptions: {
                defaultColumn: dasTotalsCellEditor,
                onRowUpdated: onRecordUpdated,
                skipPageReset,
                editorsData: {
                    shouldShowValues: showValues,
                    suggestionsList: dataSourceNames.map((x) => ({ name: x.name, type: "Data set items", value: x.name })),
                    totalsList: totals,
                    onTextAreaCaretPositionChanged: (position: number) => totalsDispatch(ACTIONS.SET_EDITOR_CARET_POSITION.withPayload(position)),
                }
            }
        }} />
        <TPEBasicModal
            className="totalsDeleteConfirmModal"
            visible={pendingRemove != null}
            action={ACTION_TYPE.YES_NO}
            title="Remove total"
            onCancel={() => setPendingRemove(undefined)}
            onConfirm={confirmRemove}
            primaryButtonDisabledLabel={isDeleting ? "Deleting..." : undefined}
        >
            <span>Are you sure you want to remove total #{pendingRemove?.visibleSequence} {pendingRemove?.description}?</span>
        </TPEBasicModal>
    </div>;
}
